import { motion, AnimatePresence } from "framer-motion";

interface PointsBarProps {
  current: number;
  max: number;
  label?: string;
}

export function PointsBar({ current, max, label = "积分" }: PointsBarProps) {
  const percentage = Math.min((current / Math.max(max, 1)) * 100, 100);

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <span className="text-lg">⚡</span>
          {label}
        </span>
        <span className="text-sm font-bold text-primary">
          {current} / {max}
        </span>
      </div>
      <div className="relative h-6 overflow-hidden rounded-full bg-muted border-2 border-border">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="h-full rounded-full bg-gradient-to-r from-accent to-primary"
        />
      </div>
    </div>
  );
}

interface StarBurstProps {
  show: boolean;
  onComplete?: () => void;
}

export function StarBurst({ show, onComplete }: StarBurstProps) {
  const confettiColors = [
    "#e03e2d",
    "#f5a623",
    "#1e6fcc",
    "#2e9e6b",
    "#7c4dcc",
    "#f472b6",
    "#06b6d4",
  ];

  return (
    <AnimatePresence
      onExitComplete={onComplete}
    >
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 pointer-events-none overflow-hidden"
        >
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              initial={{
                opacity: 0,
                scale: 0,
                x: "50vw",
                y: "50vh",
              }}
              animate={{
                opacity: [0, 1, 1, 0],
                scale: [0, 1.2, 0.8],
                x: `${Math.random() * 100}vw`,
                y: `${Math.random() * 100}vh`,
                rotate: Math.random() * 720 - 360,
              }}
              transition={{
                duration: 1.5 + Math.random() * 0.5,
                delay: i * 0.02,
                ease: "easeOut",
              }}
              className="absolute w-3 h-3 rounded-sm"
              style={{
                backgroundColor: confettiColors[i % confettiColors.length],
              }}
            />
          ))}

          {[...Array(20)].map((_, i) => (
            <motion.div
              key={`star-${i}`}
              initial={{
                opacity: 0,
                scale: 0,
                x: "50vw",
                y: "50vh",
              }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 2, 0.5],
                x: `${20 + Math.random() * 60}vw`,
                y: `${10 + Math.random() * 80}vh`,
                rotate: Math.random() * 360,
              }}
              transition={{
                duration: 1.2,
                delay: i * 0.05,
                ease: "easeOut",
              }}
              className="absolute text-4xl"
            >
              {i % 4 === 0 ? "⭐" : i % 4 === 1 ? "✨" : i % 4 === 2 ? "🌟" : "💫"}
            </motion.div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface StreakBadgeProps {
  count: number;
  size?: "sm" | "md" | "lg";
}

export function StreakBadge({ count, size = "md" }: StreakBadgeProps) {
  const sizeClasses = {
    sm: "text-base px-3 py-1.5",
    md: "text-lg px-4 py-2",
    lg: "text-2xl px-6 py-3",
  };

  const emojiSizes = {
    sm: "text-xl",
    md: "text-2xl",
    lg: "text-4xl",
  };

  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 400, damping: 20 }}
      className={`inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-accent to-primary text-primary-foreground font-bold shadow-lg ${sizeClasses[size]}`}
    >
      <span className={emojiSizes[size]}>🔥</span>
      <span>{count} 天</span>
    </motion.div>
  );
}